import { useTranslation } from 'react-i18next';
import type { CountReceipt } from '../../types';

function statusLabels(receipt: CountReceipt, t: (key: string) => string): string[] {
  const keys = receipt.statusKeys ?? [];
  if (keys.length) {
    return keys.map(key => t(`countReceipt.status.${key}`));
  }
  return receipt.statusesIncluded ?? [];
}

function channelLine(receipt: CountReceipt, t: (key: string) => string): string {
  const excluded = receipt.channelsExcluded ?? [];
  const included = receipt.channelsIncluded ?? [];
  if (!excluded.length || !included.length) return t('countReceipt.allChannels');
  return included.join(', ');
}

function cancellationLine(receipt: CountReceipt, t: (key: string) => string): string {
  if (receipt.population === 'served') return t('countReceipt.servedOnly');
  if (receipt.population === 'metrics') return t('countReceipt.metricsOwnRules');
  return receipt.excludesCancellations
    ? t('countReceipt.cancellationsOut')
    : t('countReceipt.cancellationsIn');
}

/**
 * What a count read, in the context bar's words: "All statuses" when no
 * status filter applied (every status, as Performance counts them), else
 * how many statuses were counted.
 */
function statusSummary(receipt: CountReceipt, count: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (receipt.statusEvery) return t('chrome:context.filters.allStatuses');
  return t('countReceipt.statusCount', { count });
}

export function CountReceiptLine({ receipts }: { receipts?: CountReceipt[] | null }) {
  const { t } = useTranslation('assistant');
  const rows = (receipts ?? []).filter(receipt => receipt.windowLocal || receipt.statusKeys);
  if (!rows.length) return null;

  return (
    <div className="count-receipt">
      {rows.map((receipt, index) => {
        const statuses = statusLabels(receipt, t);
        const summary = [
          receipt.windowLocal,
          channelLine(receipt, t),
          statusSummary(receipt, statuses.length, t),
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <details key={`${receipt.tool ?? 'count'}-${index}`}>
            <summary>{t('countReceipt.title')}: {summary}</summary>
            <p>
              {t('countReceipt.channels')}: {channelLine(receipt, t)}
            </p>
            <p>
              {t('countReceipt.statuses')}: {statuses.join(', ')}
            </p>
            <p>
              {cancellationLine(receipt, t)}
              {receipt.excludesWalkInChannel ? ` · ${t('countReceipt.walkInChannelOut')}` : ''}
              {receipt.excludesWalkInStatus ? ` · ${t('countReceipt.walkInStatusOut')}` : ''}
              {receipt.shift && receipt.shift !== 'all'
                ? ` · ${t('countReceipt.shift')}: ${receipt.shift}`
                : ''}
            </p>
          </details>
        );
      })}
    </div>
  );
}
